import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import {
  getAdminUsers,
  getAccessLogsByUserId,
  getCustomerById,
} from '../firebase/firestore';
import { AdminUser } from '../firebase/types';
import CompanyStatusIcon from '../components/CompanyStatusIcon';

interface AccessHistoryByCompany {
  companyId: string;
  companyName: string;
  totalCount: number;
  lastAccessTime: Date | null;
  status?: 'uncontracted' | 'new' | 'existing' | 'revived';
}

interface UserAccessData {
  user: AdminUser;
  accessHistory: AccessHistoryByCompany[];
  totalAccess: number;
}

const AdminAccessLogsPage: React.FC = () => {
  const { logout, user, adminUser } = useAuth();
  const [usersAccessData, setUsersAccessData] = useState<UserAccessData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadAccessLogs = async () => {
      // 権限チェック：adminロールのみアクセス可能
      if (adminUser?.role !== 'admin') {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');

        // 1. adminUsersから担当社員ユーザー一覧を取得
        const adminUsers = await getAdminUsers();

        // 2. 各ユーザーのaccessLogsを取得し、企業ごとに集計
        const usersData: UserAccessData[] = await Promise.all(
          adminUsers.map(async adminUser => {
            // 各ユーザーのaccessLogsを取得
            const accessLogs = await getAccessLogsByUserId(
              adminUser.userId || adminUser.id || ''
            );

            // 企業ごとに集計（companyIdでグループ化、countを合計）
            const companyMap = new Map<
              string,
              { totalCount: number; lastAccessTime: Date | null }
            >();

            accessLogs.forEach(log => {
              const existing = companyMap.get(log.companyId);
              if (existing) {
                existing.totalCount += log.count;
                if (
                  !existing.lastAccessTime ||
                  log.lastAccessTime > existing.lastAccessTime
                ) {
                  existing.lastAccessTime = log.lastAccessTime;
                }
              } else {
                companyMap.set(log.companyId, {
                  totalCount: log.count,
                  lastAccessTime: log.lastAccessTime,
                });
              }
            });

            // 企業名とステータスを取得
            const accessHistory: AccessHistoryByCompany[] = await Promise.all(
              Array.from(companyMap.entries()).map(
                async ([companyId, data]) => {
                  let companyName = '企業名不明';
                  let status:
                    | 'uncontracted'
                    | 'new'
                    | 'existing'
                    | 'revived'
                    | undefined;
                  try {
                    const customer = await getCustomerById(companyId);
                    if (customer) {
                      companyName = customer.customerData.companyName;
                      status = customer.customerData.status;
                    }
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn(
                      `Failed to get company name for ${companyId}:`,
                      err
                    );
                  }
                  return {
                    companyId,
                    companyName,
                    totalCount: data.totalCount,
                    lastAccessTime: data.lastAccessTime,
                    status,
                  };
                }
              )
            );

            // 総アクセス数を計算（全企業のアクセス回数の合計）
            const totalAccess = accessHistory.reduce(
              (sum, history) => sum + history.totalCount,
              0
            );

            return {
              user: adminUser,
              accessHistory,
              totalAccess,
            };
          })
        );

        setUsersAccessData(usersData);
      } catch (err) {
        setError('アクセス履歴の取得に失敗しました');
        // eslint-disable-next-line no-console
        console.error('Error loading access logs:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccessLogs();
  }, [adminUser]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin/login';
  };

  const handleCompanyClick = (companyId: string) => {
    window.location.href = `/admin/dashboard/${companyId}`;
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '-';
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 権限チェック：adminロールのみアクセス可能
  if (adminUser?.role !== 'admin') {
    return (
      <ProtectedRoute>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              アクセス権限がありません
            </h1>
            <p className="text-gray-600 mb-4">
              このページは管理者のみアクセスできます。
            </p>
            <button
              onClick={() => (window.location.href = '/admin/companies')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              会社一覧に戻る
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-100">
        {/* ヘッダー */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <h1 className="text-lg font-bold text-gray-900">
                    アカウント使用回数管理
                  </h1>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {user && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{user.email}</span>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      ログアウト
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* メインコンテンツ */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">読み込み中...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {usersAccessData.length > 0 ? (
                usersAccessData.map(userData => (
                  <div
                    key={userData.user.id || userData.user.userId}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                  >
                    {/* カードヘッダー */}
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                      <h2 className="text-xl font-bold text-gray-900">
                        {userData.user.name || userData.user.email}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        総アクセス数: {userData.totalAccess}
                      </p>
                    </div>

                    {/* カードコンテンツ */}
                    <div className="p-6">
                      {userData.accessHistory.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 border-b border-gray-200">
                                  会社名
                                </th>
                                <th className="px-6 py-3 text-center text-sm font-medium text-gray-600 border-b border-gray-200">
                                  ステータス
                                </th>
                                <th className="px-6 py-3 text-right text-sm font-medium text-gray-600 border-b border-gray-200">
                                  アクセス回数
                                </th>
                                <th className="px-6 py-3 text-right text-sm font-medium text-gray-600 border-b border-gray-200">
                                  最終アクセス
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {userData.accessHistory.map((history, index) => (
                                <tr
                                  key={history.companyId}
                                  className={
                                    index !== userData.accessHistory.length - 1
                                      ? 'border-b border-gray-200'
                                      : ''
                                  }
                                >
                                  <td className="px-6 py-4">
                                    <button
                                      onClick={() =>
                                        handleCompanyClick(history.companyId)
                                      }
                                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
                                    >
                                      {history.companyName}
                                    </button>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <CompanyStatusIcon
                                      status={history.status}
                                      size="sm"
                                    />
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-gray-600">
                                    {history.totalCount}回
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-gray-500">
                                    {formatDate(history.lastAccessTime)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-12">
                          <p className="text-center text-gray-400">
                            アクセス履歴がありません
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <p className="text-gray-500">
                    ユーザーが見つかりませんでした
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default AdminAccessLogsPage;
